import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const startGoogle = () => {
  sessionStorage.setItem("skd_after_login", window.location.pathname + window.location.search);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const AuthModal = () => {
  const { authOpen, setAuthOpen, loginSuccess } = useStore();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setStep(1); setCode(""); };
  const close = () => { setAuthOpen(false); setTimeout(reset, 300); };

  const requestOtp = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return toast.error("Enter a valid email address");
    setLoading(true);
    try {
      await api.post("/auth/request-otp", { channel: "email", email: email.trim(), name });
      toast.success("Code sent — please check your inbox (and Spam folder)");
      setStep(2);
    } catch (e) {
      const st = e.response?.status;
      toast.error(e.response?.data?.detail || (st ? `Could not send code (error ${st}). Please try again or continue with Google.` : "Network issue — please check your connection and try again."));
    } finally { setLoading(false); }
  };

  const verify = async () => {
    if (!code) return toast.error("Enter the code");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { channel: "email", email: email.trim(), name, code });
      await loginSuccess(data.token, data.user);
      toast.success(`Welcome${data.user.name ? ", " + data.user.name : ""}!`);
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setLoading(false); }
  };

  const inp = "bg-[#241611] border-[#3B251D] text-[#FDFBF7]";
  return (
    <Dialog open={authOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="auth-modal" className="bg-[#1A0F0B] border border-[#3B251D] text-[#FDFBF7] max-w-md p-0 overflow-hidden">
        <DialogTitle className="sr-only">Sign in to SCHOKODALE</DialogTitle>
        <DialogDescription className="sr-only">Sign in with Google or a one-time code sent to your email.</DialogDescription>
        <div className="bg-[#120A07] p-8 text-center border-b border-[#3B251D]">
          <img src="/brand/emblem.png" alt="SCHOKODALE" className="h-16 w-16 rounded-full object-cover mx-auto mb-3 ring-1 ring-[#D4AF37]/40 animate-float" />
          <h2 className="font-display text-3xl gold-gradient-text">Welcome</h2>
          <p className="text-[#8C7B6E] text-sm mt-2">Passwordless sign-in — no passwords, ever.</p>
        </div>
        <div className="p-8">
          {step === 1 ? (
            <>
              <button data-testid="auth-google-btn" onClick={startGoogle}
                className="w-full h-12 rounded-xl bg-white text-[#1F1F1F] font-semibold text-sm flex items-center justify-center gap-3 hover:bg-[#F3EDE4] transition-colors">
                <GoogleIcon /> Continue with Google
              </button>
              <div className="flex items-center gap-3 my-6 text-[11px] uppercase tracking-widest text-[#8C7B6E]">
                <span className="h-px flex-1 bg-[#3B251D]" /> or use email <span className="h-px flex-1 bg-[#3B251D]" />
              </div>
              <label className="text-xs text-[#8C7B6E] uppercase tracking-wider">Your name (optional)</label>
              <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Aarav Mehta" className={`mt-1 mb-4 ${inp}`} />
              <label className="text-xs text-[#8C7B6E] uppercase tracking-wider">Email address</label>
              <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && requestOtp()} placeholder="you@email.com" className={`mt-1 ${inp}`} />
              <Button data-testid="auth-request-otp-btn" onClick={requestOtp} disabled={loading} className="btn-outline-gold w-full mt-5 h-12 rounded-xl">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Email me a sign-in code"}
              </Button>
            </>
          ) : (
            <>
              <button onClick={() => setStep(1)} className="flex items-center gap-1 text-[#8C7B6E] text-sm mb-4 hover:text-[#D4AF37]"><ArrowLeft size={14} /> Back</button>
              <p className="text-[#C8B8AB] text-sm mb-4">Enter the 6-digit code sent to <span className="text-[#D4AF37]">{email}</span></p>
              <Input data-testid="auth-otp-input" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verify()} placeholder="______" maxLength={6}
                className={`${inp} text-center text-2xl tracking-[0.5em] font-mono-lux h-14`} />
              <Button data-testid="auth-verify-btn" onClick={verify} disabled={loading} className="btn-gold w-full mt-6 h-12 rounded-xl">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Verify & continue"}
              </Button>
              <button onClick={requestOtp} className="text-[#8C7B6E] text-xs mt-4 w-full text-center hover:text-[#D4AF37]" data-testid="auth-resend">Resend code</button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
